import { useState } from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AppConnectionsIcon from '@mui/icons-material/CloudQueueOutlined'
import CollapseVenueInfoIcon from '@mui/icons-material/ExpandLess'
import ExpandVenueInfoIcon from '@mui/icons-material/ExpandMore'
import ManageUsersIcon from '@mui/icons-material/PeopleOutline'
import LaunchAppIcon from '@mui/icons-material/RocketLaunchOutlined'
import VenueSettingsIcon from '@mui/icons-material/SettingsOutlined'

import useAppStore from '$appStore'
import { PermissionType, VenueSettings } from 'src/utils/appContext'

import VenueInfoContent from './VenueInfoContent'
import {
  CONNECTIONS_NAV_TARGET,
  MANAGE_USERS_NAV_TARGET,
  VENUE_ONBOARDING_NAV_TARGET,
  VENUE_SETTINGS_NAV_TARGET,
} from './navTargets'

type VenueSectionProps = {
  readonly onNavigate: (target: string) => void
}

/**
 * The currently selected venue, shown prominently, with its venue-scoped
 * actions beneath it (venue settings, app connections, manage users, venue
 * onboarding) gated by the user's permissions and the venue's settings.
 *
 * Renders nothing when there is no active venue. Owns the local expand state for
 * the inline venue-information panel, which collapses again whenever the flyout
 * closes (the surface unmounts this section), so the info appears in context
 * rather than in a separate dialog.
 */
const VenueSection = ({ onNavigate }: VenueSectionProps) => {
  const activeCompany = useAppStore((state) => state.activeCompany)
  const venues = useAppStore((state) => state.venues)

  const [isVenueInfoExpanded, setIsVenueInfoExpanded] = useState(false)

  const venueHasLaunchAppEnabled = !!activeCompany?.setting(
    VenueSettings.LaunchAppUrl
  )

  // Toggle the venue details inline beneath the venue name, rather than opening
  // a separate dialog, so the extra information appears in context.
  const handleToggleVenueInfo = () => {
    setIsVenueInfoExpanded((expanded) => !expanded)
  }

  if (!activeCompany?.name) {
    return null
  }

  const hasManagerPermission = activeCompany.hasPermission(
    PermissionType.Manager
  )

  return (
    <>
      <MenuList>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          paddingLeft={2}
          paddingRight={1}
          paddingBottom={1}
        >
          <Typography
            variant="subtitle1"
            fontWeight={400}
            fontSize="1.4rem"
            lineHeight="2.5"
            noWrap
            // Native tooltip so an ellipsized name is still readable.
            title={activeCompany.name}
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {activeCompany.name}
          </Typography>
          <Tooltip
            title={
              isVenueInfoExpanded
                ? 'Hide venue information'
                : 'Venue information'
            }
          >
            <IconButton
              onClick={handleToggleVenueInfo}
              aria-label={
                isVenueInfoExpanded
                  ? 'Hide venue information'
                  : 'Show venue information'
              }
              aria-expanded={isVenueInfoExpanded}
              size="small"
            >
              {isVenueInfoExpanded ? (
                <CollapseVenueInfoIcon fontSize="small" />
              ) : (
                <ExpandVenueInfoIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={isVenueInfoExpanded} unmountOnExit>
          <Box paddingLeft={2} paddingRight={2} paddingBottom={1}>
            <VenueInfoContent />
          </Box>
        </Collapse>
        {hasManagerPermission && (
          <MenuItem onClick={() => onNavigate(VENUE_SETTINGS_NAV_TARGET)}>
            <ListItemIcon>
              <VenueSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Venue settings</ListItemText>
          </MenuItem>
        )}
        {hasManagerPermission && (
          <MenuItem onClick={() => onNavigate(CONNECTIONS_NAV_TARGET)}>
            <ListItemIcon>
              <AppConnectionsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>App connections</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => onNavigate(MANAGE_USERS_NAV_TARGET)}>
          <ListItemIcon>
            <ManageUsersIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {hasManagerPermission ? 'Manage users' : 'View permissions'}
          </ListItemText>
        </MenuItem>
        {venueHasLaunchAppEnabled && (
          <MenuItem onClick={() => onNavigate(VENUE_ONBOARDING_NAV_TARGET)}>
            <ListItemIcon>
              <LaunchAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Venue onboarding</ListItemText>
          </MenuItem>
        )}
      </MenuList>

      <Divider />
    </>
  )
}

export default VenueSection
